<template>
  <Menu as="div" class="relative inline-block text-left" v-slot="{ open }">
    <div ref="menuButtonRef">
      <MenuButton :disabled="disabled">
        <slot />
      </MenuButton>
    </div>

    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div v-if="open" ref="menuItemsRef" class="z-[100]" :class="{ 'w-fit': inset }">
          <MenuItems
            class="origin-top-right bg-neutral-white focus:outline-none cursor-pointer overflow-y-auto"
            :class="{
              'w-[230px]': inset && wide,
              'w-[222px]': inset && !wide,
              'rounded-[10px] border border-neutral-300 p-1.5 shadow-[0_14px_36px_rgba(29,36,51,0.20)]': inset,
              'w-56 divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5': !inset,
            }"
            static
          >
            <div :class="{ 'py-1': !inset }">
              <MenuItem
                v-for="(option, index) in options"
                :as="'div'"
                :key="index"
                class="flex flex-col text-xsm font-normal"
              >
                <div v-tooltip="option.disabled ? option.tooltip || 'Store is inactive' : ''" class="w-full">
                  <a
                    @click="!option.disabled && $emit(option.value, option.metadata)"
                    class="block w-full"
                    :class="[
                      inset ? 'rounded-md px-3 py-[9px] text-[13px] font-medium' : 'px-4 py-2 text-sm',
                      {
                        'bg-neutral-white hover:bg-neutral-100': !option.emphasis,
                        'bg-primary-100 hover:bg-primary-100 font-bold text-primary-700': option.emphasis === 'primary',
                        'bg-success-100 hover:bg-success-100 font-semibold text-success-700':
                          option.emphasis === 'success',
                        [option.color ?? '']: !option.emphasis,
                        'opacity-50 cursor-not-allowed': option.disabled,
                      },
                    ]"
                    role="menuitem"
                    :aria-disabled="option.disabled"
                    :tabindex="option.disabled ? -1 : 0"
                  >
                    <span
                      class="flex items-center"
                      :class="{ 'justify-between gap-2': inset }"
                      :style="inset ? undefined : { color: option.color }"
                    >
                      <span class="flex items-center">
                        <span v-if="inset && option.dot" class="mr-2.5 h-2 w-2 rounded-full bg-current"></span>
                        <component
                          v-else
                          :is="AvailableIcons[option.icon]"
                          class="fill-current"
                          :class="inset ? 'mr-2.5 h-[15px] w-[15px]' : 'h-4 w-4 mr-2'"
                        />
                        {{ option.label }}
                      </span>
                      <span
                        v-if="inset && option.badge"
                        class="rounded-[3px] bg-secondary-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-[.03em] text-secondary-700"
                      >
                        {{ option.badge }}
                      </span>
                    </span>
                  </a>
                </div>
              </MenuItem>
            </div>
          </MenuItems>
        </div>
      </transition>
    </Teleport>
  </Menu>
</template>

<script setup lang="ts">
import { ref, type PropType, watch } from 'vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { createPopper, type Placement } from '@popperjs/core';
import * as Icons from '../icons';

const AvailableIcons: Record<string, any> = Icons;

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  inset: {
    type: Boolean,
    default: false,
  },
  horizontalOffset: {
    type: Number,
    default: 0,
  },
  options: {
    type: Array as PropType<
      {
        icon: string;
        label: string;
        value: string;
        badge?: string;
        dot?: boolean;
        emphasis?: 'primary' | 'success';
        metadata?: unknown;
        color?: string;
        disabled?: boolean;
        tooltip?: string;
      }[]
    >,
    default: () => [],
  },
  placement: {
    type: String as PropType<Placement>,
    default: 'bottom',
  },
  wide: {
    type: Boolean,
    default: false,
  },
});

const menuButtonRef = ref<HTMLElement | null>(null);
const menuItemsRef = ref<HTMLElement | null>(null);

function initPopper() {
  if (!menuButtonRef.value || !menuItemsRef.value) return;

  const popper = createPopper(menuButtonRef.value, menuItemsRef.value, {
    placement: props.placement,
    modifiers: [{ name: 'offset', options: { offset: [props.horizontalOffset, 0] } }],
  });

  popper.forceUpdate();
}

function openMenu() {
  menuButtonRef.value?.querySelector('button')?.click();
}

defineExpose({ openMenu });

watch(menuItemsRef, newVal => {
  if (newVal) {
    initPopper();
  }
});
</script>
